import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { assessmentAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  Brain, 
  Sparkles, 
  Heart, 
  Activity,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Target,
  Smile,
  Frown,
  Meh
} from 'lucide-react';
import { toast } from 'sonner';

const Assessment = () => {
  const { user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [assessmentStarted, setAssessmentStarted] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchData();
  }, [user?.employee_id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [questionsRes, historyRes] = await Promise.all([
        assessmentAPI.getQuestions(),
        assessmentAPI.getHistory(user?.employee_id || '100001')
      ]);
      setQuestions(questionsRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Assessment fetch error:', error);
      toast.error('Failed to load assessment data');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      const formattedAnswers = questions.map(q => ({
        question: q.question,
        answer: answers[q.id] || 'Not answered'
      }));

      const response = await assessmentAPI.submit({
        questions: questions,
        answers: formattedAnswers
      });

      setResult(response.data.analysis);
      toast.success('Assessment completed!');
      fetchData();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const progress = (Object.keys(answers).length / questions.length) * 100;

  const getStressIcon = (level) => {
    switch (level) {
      case 'low': return <Smile className="w-5 h-5 text-emerald-500" />;
      case 'medium': return <Meh className="w-5 h-5 text-amber-500" />;
      case 'high': return <Frown className="w-5 h-5 text-red-500" />;
      default: return <Meh className="w-5 h-5 text-slate-500" />;
    }
  };

  const getStressColor = (level) => {
    switch (level) {
      case 'low': return 'bg-emerald-100 text-emerald-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      case 'high': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading assessment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="assessment-page">
      {/* Header */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 bg-gradient-to-br from-violet-500 to-purple-600 text-white border-none">
          <CardContent className="p-8">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <Brain className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Psychological Assessment
                </h2>
                <p className="text-violet-100 mb-4">
                  Complete this wellness assessment to get AI-powered insights about your mental health, stress levels, and work-life balance.
                </p>
                {!assessmentStarted && !result && (
                  <Button 
                    onClick={() => setAssessmentStarted(true)}
                    className="bg-white text-violet-600 hover:bg-violet-50"
                    data-testid="start-assessment-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Start Assessment
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Assessment Stats
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Completed</span>
                <Badge className="bg-indigo-100 text-indigo-700">{history.length}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Questions</span>
                <Badge variant="outline">{questions.length}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">AI Analysis</span>
                <Badge className="bg-violet-100 text-violet-700">GPT-5.2</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Assessment Form */}
      {assessmentStarted && !result && (
        <Card data-testid="assessment-form">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Question {currentQuestion + 1} of {questions.length}
                </CardTitle>
                <CardDescription>Answer honestly for accurate insights</CardDescription>
              </div>
              <Badge className="bg-violet-100 text-violet-700">
                {Math.round(progress)}% Complete
              </Badge>
            </div>
            <Progress value={progress} className="mt-4" />
          </CardHeader>
          <CardContent className="pt-6">
            {questions[currentQuestion] && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-slate-900">
                  {questions[currentQuestion].question}
                </h3>
                
                <RadioGroup
                  value={answers[questions[currentQuestion].id] || ''}
                  onValueChange={(value) => handleAnswer(questions[currentQuestion].id, value)}
                  className="space-y-3"
                >
                  {questions[currentQuestion].options?.map((option, index) => (
                    <div key={index} className="assessment-option">
                      <RadioGroupItem 
                        value={option} 
                        id={`option-${index}`}
                        data-testid={`option-${index}`}
                      />
                      <Label 
                        htmlFor={`option-${index}`} 
                        className="flex-1 cursor-pointer font-normal"
                      >
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>

                <div className="flex items-center justify-between pt-6 border-t border-slate-100">
                  <Button
                    variant="outline"
                    onClick={handlePrevious}
                    disabled={currentQuestion === 0}
                    data-testid="prev-question-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Previous
                  </Button>
                  
                  {currentQuestion === questions.length - 1 ? (
                    <Button
                      onClick={handleSubmit}
                      disabled={submitting || Object.keys(answers).length < questions.length}
                      className="bg-gradient-to-r from-violet-600 to-indigo-600"
                      data-testid="submit-assessment-btn"
                    >
                      {submitting ? (
                        <>
                          <div className="spinner w-4 h-4 mr-2" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Get AI Analysis
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleNext}
                      disabled={!answers[questions[currentQuestion].id]}
                      data-testid="next-question-btn"
                    >
                      Next
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {result && (
        <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-white" data-testid="assessment-result">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-indigo-500 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>AI Analysis Results</CardTitle>
                <CardDescription>Powered by GPT-5.2</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {typeof result === 'object' ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-white rounded-lg border border-slate-200">
                    <p className="text-sm text-slate-500 mb-1">Stress Level</p>
                    <div className="flex items-center gap-2">
                      {getStressIcon(result.stress_level)}
                      <Badge className={getStressColor(result.stress_level)}>
                        {result.stress_level}
                      </Badge>
                    </div>
                  </div>
                  <div className="p-4 bg-white rounded-lg border border-slate-200">
                    <p className="text-sm text-slate-500 mb-1">Burnout Risk</p>
                    <div className="flex items-center gap-2">
                      {result.burnout_risk === 'low' ? (
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      ) : result.burnout_risk === 'high' ? (
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                      ) : (
                        <Activity className="w-5 h-5 text-amber-500" />
                      )}
                      <Badge className={getStressColor(result.burnout_risk)}>
                        {result.burnout_risk}
                      </Badge>
                    </div>
                  </div>
                  <div className="p-4 bg-white rounded-lg border border-slate-200">
                    <p className="text-sm text-slate-500 mb-1">Engagement</p>
                    <p className="text-2xl font-bold text-indigo-600">{result.engagement || 75}%</p>
                  </div>
                  <div className="p-4 bg-white rounded-lg border border-slate-200">
                    <p className="text-sm text-slate-500 mb-1">Wellness Score</p>
                    <p className="text-2xl font-bold text-emerald-600">{result.overall_wellness_score || 78}%</p>
                  </div>
                </div>

                {result.summary && (
                  <div className="p-4 bg-white rounded-lg border border-slate-200">
                    <h4 className="font-semibold text-slate-900 mb-2">Summary</h4>
                    <p className="text-slate-600">{result.summary}</p>
                  </div>
                )}

                {result.recommendations && result.recommendations.length > 0 && (
                  <div className="p-4 bg-white rounded-lg border border-slate-200">
                    <h4 className="font-semibold text-slate-900 mb-3">Recommendations</h4>
                    <ul className="space-y-2">
                      {result.recommendations.map((rec, index) => (
                        <li key={index} className="flex items-start gap-2 text-slate-600">
                          <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <p className="text-slate-600 whitespace-pre-wrap">{result}</p>
              </div>
            )}

            <Button 
              onClick={() => {
                setAssessmentStarted(false);
                setResult(null);
                setAnswers({});
                setCurrentQuestion(0);
              }}
              variant="outline"
              className="w-full"
              data-testid="new-assessment-btn"
            >
              Take Another Assessment
            </Button>
          </CardContent>
        </Card>
      )}

      {/* History */}
      {!assessmentStarted && history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Assessment History</CardTitle>
            <CardDescription>Your previous wellness assessments</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {history.slice(0, 5).map((assessment, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                      <Brain className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Wellness Assessment</p>
                      <p className="text-sm text-slate-500">
                        {new Date(assessment.date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-indigo-100 text-indigo-700">
                    Score: {assessment.score || 75}%
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Assessment;
